import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiUsers, FiArrowRight, FiDownload, FiEdit3 } = FiIcons;

function MarketingIntro() {
  const [reflections, setReflections] = useState({
    reader: '',
    help: ''
  });

  const handleReflectionChange = (field, value) => {
    setReflections(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-8">
        <SafeIcon icon={FiHeart} className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <h1 className="font-serif text-4xl text-gray-800 mb-4">
          GospelWise Marketing
        </h1>
        <p className="text-xl text-blue-700 font-medium mb-2">
          Marketing as Ministry
        </p>
        <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
          What if marketing your book wasn't about selling, but about serving? 
          Let's reframe marketing through the lens of the gospel.
        </p>
      </div>

      {/* Core Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8 mb-8">
        <h2 className="font-serif text-2xl text-gray-800 mb-4">
          A Different Way to Think About Marketing
        </h2>
        
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6">
            <h3 className="font-semibold text-blue-700 mb-3">❌ Traditional Marketing Mindset</h3>
            <ul className="text-gray-600 space-y-2">
              <li>• "I need to convince people to buy my book"</li>
              <li>• "I have to promote myself constantly"</li>
              <li>• "Marketing feels pushy and uncomfortable"</li>
              <li>• "Success means sales numbers"</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-6">
            <h3 className="font-semibold text-emerald-700 mb-3">✅ GospelWise Marketing Mindset</h3>
            <ul className="text-gray-600 space-y-2">
              <li>• "I want to serve readers who need this message"</li>
              <li>• "I'm connecting my book with hearts that need it"</li>
              <li>• "Marketing is ministry—I'm shepherding people to help"</li>
              <li>• "Success means faithful stewardship of my calling"</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Key Principles */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-blue-100 mb-8">
        <h2 className="font-serif text-2xl text-gray-800 mb-6">
          Three GospelWise Marketing Principles
        </h2>
        
        <div className="space-y-6">
          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 rounded-full p-2 flex-shrink-0 mt-1">
              <span className="text-blue-700 font-bold text-sm">1</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Lead with Love, Not Pressure</h3>
              <p className="text-gray-600">
                Your marketing should feel like a warm invitation, not a sales pitch. 
                Share your heart, your story, and your passion for helping others.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 rounded-full p-2 flex-shrink-0 mt-1">
              <span className="text-blue-700 font-bold text-sm">2</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Focus on Service, Not Sales</h3>
              <p className="text-gray-600">
                Ask yourself: "How can I serve my readers today?" Share helpful content, 
                encourage others, and build genuine relationships.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 rounded-full p-2 flex-shrink-0 mt-1">
              <span className="text-blue-700 font-bold text-sm">3</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Trust God with the Results</h3>
              <p className="text-gray-600">
                Your job is faithful stewardship. God's job is the harvest. 
                Do your work with excellence, then trust Him with the outcome.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Reflection Questions */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-blue-100 mb-8">
        <h2 className="font-serif text-2xl text-gray-800 mb-6">
          Reflection Questions
        </h2>
        <p className="text-gray-600 mb-6">
          Take a few minutes to think about your readers and how your book can serve them.
        </p>

        <div className="space-y-6">
          <div>
            <label className="block font-semibold text-gray-800 mb-3">
              Who is one specific reader this book is for?
            </label>
            <textarea
              value={reflections.reader}
              onChange={(e) => handleReflectionChange('reader', e.target.value)}
              placeholder="Think of a real person (friend, family member, or someone you've met) who would benefit from your book. What's their name? What's their situation?"
              className="w-full p-4 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors h-24 resize-none"
            />
          </div>

          <div>
            <label className="block font-semibold text-gray-800 mb-3">
              What's one way this book could help them?
            </label>
            <textarea
              value={reflections.help}
              onChange={(e) => handleReflectionChange('help', e.target.value)}
              placeholder="How will your book change their life? What specific problem will it solve or what hope will it provide?"
              className="w-full p-4 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors h-24 resize-none"
            />
          </div>
        </div>

        {reflections.reader && reflections.help && (
          <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-emerald-700 font-medium">
              ✅ Beautiful! Keep this person in mind as you write and share your work. 
              They're waiting for the hope and help your book provides.
            </p>
          </div>
        )}
      </div>

      {/* Download & Next Steps */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-blue-100 text-center">
        <h3 className="font-serif text-2xl text-gray-800 mb-4">
          Ready to Dive Deeper?
        </h3>
        <p className="text-gray-600 mb-6">
          Get the complete GospelWise Marketing framework with practical steps 
          and templates to share your book with love and authenticity.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
            <SafeIcon icon={FiDownload} className="w-4 h-4" />
            <span>Download Marketing Guide</span>
          </button>

          <Link 
            to="/next-steps"
            className="bg-emerald-600 text-white px-6 py-3 rounded-xl hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2"
          >
            <span>Continue Your Journey</span>
            <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Encouragement */}
      <div className="text-center mt-8">
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <p className="text-gray-700 italic text-lg leading-relaxed">
            "You don't need to hustle—just take the next faithful step. 
            Your readers are waiting for what you have to offer."
          </p>
        </div>
      </div>
    </div>
  );
}

export default MarketingIntro;